import { useMemo, useState, type FormEvent, type KeyboardEvent } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Supplier } from "@/features/inventory/services/suppliersApi";
import { InputLookup } from "@/features/inventory/components/inputs/InputLookup";
import type { AccountSummaryDto } from "@/features/accounting/services/accountingApi";
import type { CreateSupplierInvoiceRequest } from "../services/invoicingApi";
import { Plus, Trash2 } from "lucide-react";

type DraftLine = { lineType: number; inputId: string; inputLabel: string; postingAccountId: string; description: string; quantity: string; unit: string; unitCost: string; vatPercentage: string };
const emptyLine = (): DraftLine => ({ lineType: 2, inputId: "", inputLabel: "", postingAccountId: "", description: "", quantity: "1", unit: "Service", unitCost: "", vatPercentage: "0" });
const today = () => new Date().toISOString().slice(0, 10);
const money = new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" });

const numericValue = (value: string) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeDecimal = (value: string, max?: number) => {
  const [whole = "", ...decimalParts] = value.replace(/[^\d.]/g, "").split(".");
  const normalized = decimalParts.length > 0 ? `${whole}.${decimalParts.join("")}` : whole;
  if (max === undefined || normalized === "" || normalized === ".") return normalized;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > max ? String(max) : normalized;
};

const handleDecimalKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  const allowedKeys = new Set([
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "Tab",
  ]);

  if (allowedKeys.has(event.key) || /^\d$/.test(event.key)) return;

  if (event.key === ".") {
    const input = event.currentTarget;
    const selection = input.value.slice(input.selectionStart ?? 0, input.selectionEnd ?? 0);
    if (!input.value.includes(".") || selection.includes(".")) return;
  }

  event.preventDefault();
};

const lineTotal = (line: DraftLine) => {
  const subtotal = numericValue(line.quantity) * numericValue(line.unitCost);
  return subtotal + subtotal * (numericValue(line.vatPercentage) / 100);
};

export function CreateSupplierInvoiceDialog({ open, onOpenChange, suppliers, accounts, isSubmitting, onSubmit }: {
  open: boolean; onOpenChange: (open: boolean) => void; suppliers: Supplier[];
  accounts: AccountSummaryDto[]; isSubmitting: boolean; onSubmit: (payload: CreateSupplierInvoiceRequest) => void;
}) {
  const [supplierId, setSupplierId] = useState("");
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(today());
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const update = (index: number, patch: Partial<DraftLine>) => setLines((current) => current.map((line, i) => i === index ? { ...line, ...patch } : line));
  const invoiceTotal = useMemo(() => lines.reduce((sum, line) => sum + lineTotal(line), 0), [lines]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({ supplierId, supplierInvoiceNumber: supplierInvoiceNumber || undefined, invoiceDate, dueDate,
      items: lines.map((line) => ({ lineType: line.lineType, inputId: line.lineType === 1 ? line.inputId : null,
        postingAccountId: line.postingAccountId, description: line.description, quantity: Number(line.quantity),
        unit: line.unit, unitCost: Number(line.unitCost), vatPercentage: Number(line.vatPercentage || 0) })) });
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="flex h-[86vh] max-h-[98dvh] w-[calc(100vw-2rem)] min-h-0 flex-col overflow-hidden p-0 sm:min-h-[760px] sm:!max-w-[1500px]">
      <DialogHeader className="border-b px-6 py-5 lg:px-8">
        <DialogTitle>New Supplier Invoice</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6 lg:px-8">
          <section className="rounded-lg border bg-muted/20 p-5">
            <div className="grid gap-5 md:grid-cols-12">
              <div className="space-y-2 md:col-span-6"><Label>Supplier</Label><Select value={supplierId} onValueChange={setSupplierId}><SelectTrigger className="h-10 w-full bg-background"><SelectValue placeholder="Select supplier" /></SelectTrigger><SelectContent>{suppliers.filter(s => s.isActive).map(s => <SelectItem key={s.id} value={s.id}>{s.supplierName}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2 md:col-span-3"><Label>Supplier invoice #</Label><Input className="h-10 bg-background" value={supplierInvoiceNumber} onChange={e => setSupplierInvoiceNumber(e.target.value)} /></div>
              <div className="space-y-2 md:col-span-3"><Label>Invoice date</Label><Input className="h-10 bg-background" type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} required /></div>
              <div className="space-y-2 md:col-span-3"><Label>Due date</Label><Input className="h-10 bg-background" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} required /></div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium">Invoice lines</h3>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setLines(current => [...current, emptyLine()])}><Plus className="h-4 w-4" />Add line</Button>
            </div>

            {lines.map((line, index) => <div key={index} className="rounded-lg border bg-background p-5 shadow-xs">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Line {index + 1}</p>
                  <p className="text-xs text-muted-foreground">{line.lineType === 1 ? "Inventory item" : line.lineType === 3 ? "Fixed asset" : "Expense or service"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Line total</p>
                    <p className="text-sm font-semibold tabular-nums">{money.format(lineTotal(line))}</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon-sm" disabled={lines.length === 1} aria-label={`Remove line ${index + 1}`} onClick={() => setLines(current => current.filter((_, i) => i !== index))}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-12">
                <div className="space-y-2 lg:col-span-2"><Label>Type</Label><Select value={String(line.lineType)} onValueChange={v => update(index, { lineType: Number(v), inputId: "" })}><SelectTrigger className="h-10 w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">Inventory</SelectItem><SelectItem value="2">Expense / Service</SelectItem><SelectItem value="3">Fixed Asset</SelectItem></SelectContent></Select></div>
                {line.lineType === 1 && <div className="space-y-2 lg:col-span-3"><Label>Inventory item</Label><InputLookup value={line.inputId} selectedLabel={line.inputLabel} activeOnly placeholder="Select item" onSelect={item => update(index, { inputId: item.id, inputLabel: `${item.inputCode} — ${item.inputName}`, description: item.inputName, unit: item.unitOfMeasureDisplay ?? line.unit, unitCost: String(item.unitCost ?? "") })} /></div>}
                <div className={`space-y-2 ${line.lineType === 1 ? "lg:col-span-3" : "lg:col-span-4"}`}><Label>Posting account</Label><Select value={line.postingAccountId} onValueChange={v => update(index, { postingAccountId: v })}><SelectTrigger className="h-10 w-full"><SelectValue placeholder="Select GL account" /></SelectTrigger><SelectContent>{accounts.filter(a => line.lineType === 2 ? a.accountType === 5 : line.lineType === 3 ? a.accountType === 1 : a.accountType === 1 || a.accountType === 5).map(a => <SelectItem key={a.id} value={a.id}>{a.accountCode} — {a.accountName}</SelectItem>)}</SelectContent></Select></div>
                <div className={`space-y-2 ${line.lineType === 1 ? "lg:col-span-4" : "lg:col-span-6"}`}><Label>Description</Label><Input className="h-10" value={line.description} onChange={e => update(index, { description: e.target.value })} required /></div>
              </div>

              <div className="mt-4 grid items-end gap-4 sm:grid-cols-2 lg:grid-cols-12">
                <div className="space-y-2 lg:col-span-2"><Label>Quantity</Label><Input className="h-10" type="text" inputMode="decimal" pattern="[0-9]*[.]?[0-9]*" value={line.quantity} onKeyDown={handleDecimalKeyDown} onChange={e => update(index, { quantity: sanitizeDecimal(e.target.value) })} required /></div>
                <div className="space-y-2 lg:col-span-3"><Label>Unit</Label><Input className="h-10" value={line.unit} onChange={e => update(index, { unit: e.target.value })} required /></div>
                <div className="space-y-2 lg:col-span-3"><Label>Unit cost</Label><Input className="h-10" type="text" inputMode="decimal" pattern="[0-9]*[.]?[0-9]*" value={line.unitCost} onKeyDown={handleDecimalKeyDown} onChange={e => update(index, { unitCost: sanitizeDecimal(e.target.value) })} required /></div>
                <div className="space-y-2 lg:col-span-2"><Label>VAT %</Label><Input className="h-10" type="text" inputMode="decimal" pattern="[0-9]*[.]?[0-9]*" value={line.vatPercentage} onKeyDown={handleDecimalKeyDown} onChange={e => update(index, { vatPercentage: sanitizeDecimal(e.target.value, 100) })} /></div>
              </div>
            </div>)}
          </section>
        </div>

        <DialogFooter className="border-t bg-muted/20 px-6 py-4 sm:items-center sm:justify-between lg:px-8">
          <div className="text-left">
            <p className="text-xs text-muted-foreground">Invoice total</p>
            <p className="text-base font-semibold tabular-nums">{money.format(invoiceTotal)}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={isSubmitting || !supplierId || lines.some(l => !l.postingAccountId)}>{isSubmitting ? "Creating..." : "Create invoice"}</Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
}
